import React, { useState } from 'react';
import { toast } from 'sonner';
interface ConsultationFormProps {
  id?: string;
  title: string;
  variant?: 'light' | 'dark';
}
interface FormState {
  name: string;
  email: string;
  phone: string;
  zip: string;
  details: string;
}
const initialState: FormState = {
  name: '',
  email: '',
  phone: '',
  zip: '',
  details: ''
};
export function ConsultationForm({
  id,
  title,
  variant = 'light'
}: ConsultationFormProps) {
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const isDark = variant === 'dark';
  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const {
      name,
      value
    } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof FormState]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  }
  function validate(): boolean {
    const nextErrors: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) nextErrors.name = 'Name is required';
    if (!form.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nextErrors.email = 'Enter a valid email';
    }
    if (!form.zip.trim()) {
      nextErrors.zip = 'Zip code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) {
      nextErrors.zip = 'Enter a valid zip code';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      toast.success("Thanks! We'll be in touch shortly to schedule your appointment.");
      setForm(initialState);
    }, 700);
  }
  const labelClass = `block text-sm font-medium mb-1.5 ${isDark ? 'text-white/90' : 'text-brand-charcoal'}`;
  const inputBase = `w-full rounded-md border px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-brand-teal transition-colors`;
  const inputClass = isDark ? `${inputBase} bg-white/10 border-white/20 text-white placeholder:text-white/40` : `${inputBase} bg-white border-gray-300 text-brand-charcoal`;
  const errorClass = 'mt-1 text-xs text-red-500';
  return <div id={id} className={`rounded-2xl p-6 sm:p-8 shadow-xl ${isDark ? 'bg-brand-charcoal border border-white/10' : 'bg-white'}`} data-id="element-12" data-mp="9j7vpfz5lsd4">
      <h3 className={`font-display text-2xl sm:text-3xl mb-1 ${isDark ? 'text-white' : 'text-brand-charcoal'}`} data-id="element-13" data-mp="j8rg4q2ilua3">
        {title}
      </h3>
      <form onSubmit={handleSubmit} className="mt-5 space-y-4" noValidate data-id="element-14" data-mp="3hwf49u2ommbf">
        <div data-id="element-15" data-mp="3mvi1wtgc1q9d">
          <label htmlFor={`${id}-name`} className={labelClass} data-id="element-16" data-mp="3mv9pt3s1aoz8">
            Name<span className="text-brand-teal" data-id="element-17" data-mp="3fcifsg1hlkmb">*</span>
          </label>
          <input id={`${id}-name`} name="name" type="text" value={form.name} onChange={handleChange} className={inputClass} aria-required="true" aria-invalid={!!errors.name} data-id="element-18" data-mp="3aegzu3zcy1f2" />
          {errors.name && <p className={errorClass} data-id="element-19" data-mp="2wf7k15f2d9bm">{errors.name}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-20" data-mp="2wgsc8odwog6b">
          <div data-id="element-21" data-mp="2rmo8sf3umq9z">
            <label htmlFor={`${id}-email`} className={labelClass} data-id="element-22" data-mp="2rm618mbqkpmr">
              Email<span className="text-brand-teal" data-id="element-23" data-mp="2mpxt3svyhwp3">*</span>
            </label>
            <input id={`${id}-email`} name="email" type="email" value={form.email} onChange={handleChange} className={inputClass} aria-required="true" aria-invalid={!!errors.email} data-id="element-24" data-mp="2hqulq1si7edy" />
            {errors.email && <p className={errorClass} data-id="element-25" data-mp="2xhc8gewljxc7">{errors.email}</p>}
          </div>
          <div data-id="element-26" data-mp="32fdo0putdeix">
            <label htmlFor={`${id}-phone`} className={labelClass} data-id="element-27" data-mp="32e2myxsr6kvi">
              Phone
            </label>
            <input id={`${id}-phone`} name="phone" type="tel" value={form.phone} onChange={handleChange} className={inputClass} data-id="element-28" data-mp="2omxssb85mksk" />
          </div>
        </div>

        <div data-id="element-29" data-mp="2jocs6aozpz3l">
          <label htmlFor={`${id}-zip`} className={labelClass} data-id="element-30" data-mp="2jp5rwrpj7oc8">
            Project Zip Code<span className="text-brand-teal" data-id="element-31" data-mp="25p4op5lso4tl">*</span>
          </label>
          <input id={`${id}-zip`} name="zip" type="text" inputMode="numeric" value={form.zip} onChange={handleChange} className={`${inputClass} max-w-[180px]`} aria-required="true" aria-invalid={!!errors.zip} data-id="element-32" data-mp="25qxy2snsl35a" />
          {errors.zip && <p className={errorClass} data-id="element-33" data-mp="1sgmcydazul2s">{errors.zip}</p>}
        </div>

        <div data-id="element-34" data-mp="1serowq4fecuh">
          <label htmlFor={`${id}-details`} className={labelClass} data-id="element-35" data-mp="1ofso9hpq928u">
            About Your Project
          </label>
          <p className={`text-xs mb-1.5 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-36" data-mp="227gwh9wuv6kj">
            Please include any relevant details you may have, including project timeline,
            contractor, and if you have drawings and/or cabinet layout.
          </p>
          <textarea id={`${id}-details`} name="details" rows={4} value={form.details} onChange={handleChange} className={`${inputClass} resize-none`} data-id="element-37" data-mp="2c6y5a07em0kq" />
        </div>

        <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-38" data-mp="2g7rr8o1ad5az">
          *Required Fields
        </p>

        <button type="submit" disabled={submitting} className="w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-brand-teal px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-brand-tealDark disabled:opacity-60" data-id="element-39" data-mp="qsafw7zza25d">
          {submitting ? 'Submitting…' : 'Schedule Appointment'}
        </button>
      </form>
    </div>;
}